<template>
  <BaseModule
    v-bind="$props"
    class="accommodation-list"
    data-module="accommodation-list"
    :style="{
      '--card-bg-color': cardBackgroundColor,
      '--card-text-color': cardTextColor,
    }"
  >
    <header
      v-if="title || subtitle"
      class="base-title-fontsize"
    >
      <component
        :is="titleTag"
        v-if="title"
        class="tit tit-g"
      >
        {{ title }}
      </component>

      <h3
        v-if="subtitle"
        class="sub"
      >
        {{ subtitle }}
      </h3>
    </header>

    <Warning
      v-if="editing"
      text="O módulo não possui um hotel selecionado"
    >
    </Warning>

    <Loader v-else-if="loading" />

    <EmptyState
      v-if="isEmptyState"
      :title="$t('notAvailableTitle')"
      :subtitle="$t('notAvailableSubtitle')"
      :img="emptyStateImg"
    />

    <div
      v-if="showAccommodations"
      class="content"
    >
      <ul class="list">
        <li
          v-for="(room, index) in roomList"
          ref="roomRef"
          :key="index"
          :data-room-index="index"
          class="room"
        >
          <div
            v-if="room.MenorTarifa.Disponivel"
            class="room__disclaimer"
            @mouseover="showTooltip"
            @mouseleave="hideTooltip"
          >
            <span class="slb-tooltip arrow-right-down">
              {{ getDisclaimer(room) }}
            </span>
            <IconInfoTooltip />
          </div>

          <!-- Slider images container -->
          <div
            :ref="'images-' + room.Codigo"
            class="room__images swiper"
          >
            <div class="guest-max">
              Max {{ $tc('adult', room.MaximoAdultos) }}
              <span v-if="room.MaximoCriancas">
                {{ $tc('child', room.MaximoCriancas) }}
              </span>
            </div>

            <button
              class="modal-button"
              @click="openModal(room)"
            >
              <IconOpen class="text-white" />
            </button>

            <ul class="swiper-wrapper">
              <li
                v-for="(img, idx) in getGallery(room)"
                :key="idx"
                class="bg swiper-slide"
                :style="{ backgroundImage: `url('${img.Image}')` }"
              ></li>
            </ul>

            <!-- If we need navigation buttons -->
            <div
              :ref="'btn-prev-' + room.Codigo"
              class="custom-button-prev"
            >
              <IconArrow
                class="button-icon text-white"
                direction="left"
              />
            </div>
            <div
              :ref="'btn-next-' + room.Codigo"
              class="custom-button-next"
            >
              <IconArrow
                class="button-icon text-white"
                direction="right"
              />
            </div>
          </div>

          <div
            class="room__content"
            :class="{
              'room__content--see-more': room.seeMore,
            }"
          >
            <h3
              class="room-name"
              :title="room.Nome"
            >
              {{ room.Nome }}
            </h3>

            <div
              class="room-description"
              :class="{
                'room-description--empty': !hasText(room.Descricao),
              }"
            >
              <p
                :ref="'description-text-' + room.Codigo"
                class="room-description__text"
              >
                {{ room.Descricao }}
              </p>
            </div>

            <button
              class="toggle-see-more"
              :class="{
                'toggle-see-more--hidden':
                  !room.seeMoreEnabled || !hasText(room.Descricao),
              }"
              type="button"
              @click="toggleSeeMore(room)"
            >
              <span
                v-if="!room.seeMore"
                class="more"
                >{{ $t('seeMore') }}</span
              >
              <span
                v-else
                class="less"
                >{{ $t('readLess') }}</span
              >
            </button>

            <div
              v-if="showPrices"
              class="room-price"
            >
              <span
                v-if="room.MenorTarifa.Valor"
                class="price"
                :style="{ color: getPriceColor(room) }"
                v-html="getValueHTML(room.MenorTarifa.ValorFormatado)"
              ></span>

              <span
                v-if="room.MenorTarifa.TemDesconto"
                class="price--promo"
              >
                {{ room.MenorTarifa.ValorFormatadoSemDesconto }}
              </span>
            </div>

            <div class="room-buttons">
              <button
                v-if="contactButtonText"
                :ref="'contact-buttom-' + room.Codigo"
                class="cta"
                :class="{
                  vazado: !contactButtonFeatured,
                }"
                @click="manageContactButton(room)"
              >
                <span v-if="room.seeContact">
                  <IconWhatsApp
                    v-if="contactButtonType === 'whatsApp'"
                    height="24"
                    width="24"
                  />
                  <IconPhone
                    v-else
                    size="xs"
                  />
                  {{ contactButtonContent }}
                </span>
                <span v-else>{{ contactButtonText }}</span>
              </button>
              <EditorLink
                v-if="buttonText"
                :model="getLinkLetsbook(room.MenorTarifa.LinkPublico)"
                class="cta"
                @click.native="trackCustomEvent('select_item', room, index)"
              >
                {{ buttonText }}
              </EditorLink>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <template slot="modal">
      <div
        v-if="selectedAccommodation"
        class="modal-room"
      >
        <button
          class="modal-room__close"
          @click="closeModal"
        >
          <IconClose />
        </button>
        <div class="modal-room__container custom-scrollbar">
          <div class="modal-room__content">
            <div class="modal-room__content__amenities">
              <h3 class="modal-room__content__name">
                {{ selectedAccommodation.Nome }}
              </h3>
              <ul class="list-amenities">
                <li
                  v-for="(amenitie, index) in selectedAccommodation.Amenidades"
                  :key="index"
                  class="list-amenities__item"
                >
                  {{ amenitie }}
                </li>
              </ul>
            </div>
            <div class="modal-room__content__images">
              <ul class="list-images">
                <img
                  v-for="(img, index) in selectedAccommodation.Imagens.Galeria"
                  :key="index"
                  class="list-images__item"
                  :src="img"
                  alt=""
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

import getApiHotels from '@/mixins/getApiHotels';
import base from '@/mixins/base';
import IconClose from '@/components/icons/IconClose.vue';
import Loader from '@/components/Loader.vue';
import Warning from '@/components/Warning.vue';
import IconOpen from '@/components/icons/IconOpen.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconInfoTooltip from '@/components/icons/IconInfoTooltip.vue';
import IconPhone from '@/components/icons/IconPhone.vue';
import IconWhatsApp from '@/components/icons/IconWhatsApp.vue';
import { pushDataLayer } from '@/helpers/gtm';
import randomizeArray from '@/helpers/randomizeArray';
import EditorLink from './EditorLink.vue';
import EmptyState from './EmptyState.vue';
import BaseModule from './generic/BaseModule.vue';

const emptyAccommodationsUrlPath = require('@/assets/empty-accommodations.png');

export default {
  name: 'AccommodationList',
  components: {
    BaseModule,
    Loader,
    Warning,
    EditorLink,
    EmptyState,
    IconClose,
    IconOpen,
    IconArrow,
    IconInfoTooltip,
    IconPhone,
    IconWhatsApp,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    hotel: {
      type: Number,
      default: null,
    },
    showPrices: {
      type: Boolean,
      default: true,
    },
    sortingType: {
      type: String,
      default: 'highest-price',
    },
    buttonText: {
      type: String,
      default: 'Reservar online',
    },
    contactButtonFeatured: {
      type: Boolean,
      default: false,
    },
    contactButtonText: {
      type: String,
      default: '',
    },
    contactButtonType: {
      type: String,
      default: 'whatsApp',
    },
    contactButtonContent: {
      type: String,
      default: '',
    },
    cardBackgroundColor: {
      type: String,
      default: '',
    },
    cardTextColor: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      empty: false,
      loading: true,
      isMobile: false,
      mobileMaxWidth: 768,
      rooms: [],
      selectedAccommodation: null,
      selectedHotel: null,
      emptyStateImg: emptyAccommodationsUrlPath,
    };
  },
  computed: {
    roomList() {
      const rooms = [...this.rooms]; // copy to avoid side-effect on sort

      switch (this.sortingType) {
        case 'lowest-price':
          return rooms.sort(
            (room1, room2) => room1.MenorTarifa.Valor - room2.MenorTarifa.Valor,
          );
        case 'highest-price':
          return rooms.sort(
            (room1, room2) => room2.MenorTarifa.Valor - room1.MenorTarifa.Valor,
          );
        case 'highest-discount':
          return rooms.sort((room1, room2) => {
            const discount1 = this.showDiscount(room1)
              ? room1.MenorTarifa.PercentualDesconto
              : 0;
            const discount2 = this.showDiscount(room2)
              ? room2.MenorTarifa.PercentualDesconto
              : 0;
            return discount2 - discount1;
          });
        case 'random':
          return randomizeArray(rooms);
        default:
          return rooms;
      }
    },
    hasAccommodations() {
      return this.roomList.length > 0;
    },
    showAccommodations() {
      return !this.loading && this.hasAccommodations && !this.empty;
    },
    editing() {
      return typeof this.$parent === 'undefined' && !this.hotel;
    },
    isEmptyState() {
      return !this.hasAccommodations && !this.loading && !this.editing;
    },
    apiLocale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en-us';
        case 'es':
          return 'es-es';
        default:
          return 'pt-br';
      }
    },
    ...mapGetters(['settings']),
    ...mapState(['apiData']),
  },
  watch: {
    hotel() {
      this.requestAccommodations();
    },
    rooms(val) {
      if (val && val.length > 0 && !this.loading) {
        this.$nextTick(() => {
          this.bindSliderImages();
          this.manageSeeMore();
        });
      }
    },
  },
  async mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);

    this.loading = true;
    await this.getApiHotels();
    this.loading = false;

    await this.requestAccommodations();

    if (this.hotel) {
      this.selectedHotel = this.hotels.find(
        ({ Codigo }) => Codigo === this.hotel,
      );
    }

    // Inicializa o observer para o dispardo de eventos customizados
    if (window.IntersectionObserver && !this.editing) {
      this.createObserver();
    }
  },
  methods: {
    trackCustomEvent(event, room, index) {
      pushDataLayer({ ecommerce: null });
      pushDataLayer({
        event,
        ecommerce: {
          items: [
            {
              hotel_id: String(this.selectedHotel.Codigo),
              item_list_id: room.Codigo,
              item_list_name: this.selectedHotel.Destino.Cidade,
              item_name: room.Nome,
              item_id: `Q${room.Codigo}`,
              item_category: 'Quarto',
              item_category2: null, // não recebemos na API
              item_category3: this.selectedHotel.Nome,
              price: room.MenorTarifa.Valor > 0 ? room.MenorTarifa.Valor : null,
              index: index + 1,
            },
          ],
        },
      });
    },
    createObserver() {
      /** Margem relativa em que o evento é disparado */
      const verticalThreshold = '-25%';
      const options = {
        root: null,
        rootMargin: `0px 0px ${verticalThreshold} 0px`, // top left bottom right
        threshold: '0',
      };

      const observer = new IntersectionObserver(this.handleIntersect, options);

      Object.keys(this.$refs.roomRef).forEach((key) => {
        observer.observe(this.$refs.roomRef[key]);
      });
    },
    handleIntersect(entries, observer) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const { roomIndex } = entry.target.dataset;
          const room = this.roomList[roomIndex];

          this.trackCustomEvent('view_item_list', room, Number(roomIndex));

          observer.unobserve(entry.target);
        }
      });
    },
    onResize() {
      const isMobile = window.innerWidth < this.mobileMaxWidth;

      // If "device" changed...
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    },
    onlyNumbers(text) {
      return text.replace(/\D/g, '');
    },
    bindSliderImages() {
      this.roomList.forEach((room) => {
        const swiperEl = this.$refs[`images-${room.Codigo}`][0]; // esta ref é um Array
        // eslint-disable-next-line no-new
        new Swiper(swiperEl, {
          modules: [Navigation],
          slidesPerView: 1,
          slidesPerGroup: 1,
          observer: true,
          loop: room.Imagens.Galeria.length > 1,
          followFinger: false,
          allowTouchMove: false,
          navigation: {
            nextEl: this.$refs[`btn-next-${room.Codigo}`],
            prevEl: this.$refs[`btn-prev-${room.Codigo}`],
          },
        });
      });
    },
    manageSeeMore() {
      this.roomList.forEach((room) => {
        const el = this.$refs[`description-text-${room.Codigo}`];
        if (el && el[0]?.offsetHeight > 21 * 3) {
          // eslint-disable-next-line no-param-reassign
          room.seeMoreEnabled = true;
          el[0].style.webkitLineClamp = '3';
        }
      });
    },
    manageContactButton(room) {
      if (this.contactButtonContent) {
        switch (this.contactButtonType) {
          case 'link':
            window.open(this.contactButtonContent, '_blank');
            break;
          case 'whatsApp':
            window.open(
              `https://wa.me/${this.onlyNumbers(this.contactButtonContent)}`,
              '_blank',
            );
            break;
          default:
            if (this.isMobile) {
              window.open(
                `tel:${this.onlyNumbers(this.contactButtonContent)}`,
                '_blank',
              );
            }

            // eslint-disable-next-line no-param-reassign
            room.seeContact = true;
            break;
        }
      }
    },
    showMoreRooms() {
      this.visibleRoomsNumber += 6;
    },
    async requestAccommodations() {
      this.loading = true;
      if (this.hotel) {
        const urlAccommodations = `${this.settings.letsbookApiId}/acomodacoes/${this.hotel}/${this.apiLocale}`;
        if (!this.apiData[urlAccommodations]) {
          try {
            await this.$store.dispatch('loadApiData', urlAccommodations);
          } catch {
            return;
          }
        }
        this.rooms = this.apiData[urlAccommodations].map((room) => ({
          ...room,
          seeMore: false,
          seeContact: false,
          seeMoreEnabled: false,
        }));
      }
      this.loading = false;
    },
    hasText(text) {
      return typeof text === 'string' && /\S/.test(text);
    },
    showDiscount(room) {
      return room.MenorTarifa.ExibirDesconto && room.MenorTarifa.TemDesconto;
    },
    getGallery(room) {
      return room.Imagens.Galeria.map((image) => ({
        Image: image,
        CodigoQuarto: room.Codigo,
      }));
    },
    getValueHTML(formattedValue) {
      const rgx = /(\D*)(\d{1,3}(?:[.,\s]\d{3})*)((?:[.,\s]\d{2})?\D*)/; // (any)(INT)(any)
      return formattedValue.replace(rgx, '$1<span class="integer">$2</span>$3'); // $1 might be empty
    },
    getDisclaimer(room) {
      const adultos = room.MenorTarifa.NumeroAdultos;
      const noites = room.MenorTarifa.NumeroNoites;

      return this.$t('disclaimer', {
        nights: this.$tc('night', noites),
        adults: this.$tc('adult', adultos),
      });
    },
    getPriceColor(room) {
      return room.MenorTarifa.TemDesconto
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    toggleSeeMore(room) {
      // eslint-disable-next-line no-param-reassign
      room.seeMore = !room.seeMore;
    },
    openModal(room) {
      this.selectedAccommodation = room;
      document.querySelector('html').classList.add('scroll-block');
    },
    closeModal() {
      this.selectedAccommodation = null;
      document.querySelector('html').classList.remove('scroll-block');
    },
    getLinkLetsbook(link) {
      return link || this.settings.letsbookUrl.replace(/\/admin\/?$/i, '/D/');
    },
    showTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      const position = el.getBoundingClientRect();
      if (position.top < slbTooltip.clientHeight + 10) {
        slbTooltip.classList.add('open-down');
      } else {
        slbTooltip.classList.add('open-up');
      }
    },
    hideTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      slbTooltip.classList.remove('open-up', 'open-down');
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'rooms',
            label: 'Configurações',
            position: 0,
            fields: [
              {
                name: 'hotel',
                type: 'VueSelect',
                label: 'Selecione o hotel',
                helpText:
                  "As acomodações listadas são cadastradas no Letsbook em:<br>Menu &gt Let's Book &gt Hotéis &gt Quartos",
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'sortingType',
                type: 'VueSelect',
                label: 'Forma de ordenação',
                isEnabled({ hotel }) {
                  return !!hotel;
                },
                options: [
                  {
                    label: 'Menor preço',
                    value: 'lowest-price',
                  },
                  {
                    label: 'Maior preço',
                    value: 'highest-price',
                  },
                  {
                    label: 'Maior desconto',
                    value: 'highest-discount',
                  },
                  {
                    label: 'Ordem aleatória',
                    value: 'random',
                  },
                ],
              },
              {
                name: 'buttonText',
                label: 'Texto do botão reservar',
                type: 'ShortText',
                maxlength: 15,
              },
              {
                name: 'contactButtonText',
                label: 'Texto do botão de contato',
                type: 'ShortText',
                maxlength: 20,
              },
              {
                name: 'contactButtonType',
                label: 'Botão de contato',
                type: 'VueSelect',
                isEnabled({ contactButtonText }) {
                  return contactButtonText;
                },
                options: [
                  {
                    label: 'WhatsApp',
                    value: 'whatsApp',
                  },
                  {
                    label: 'Telefone',
                    value: 'phone',
                  },
                  {
                    label: 'Link',
                    value: 'link',
                  },
                ],
              },
              {
                name: 'contactButtonContent',
                label: 'Conteúdo do botão de contato',
                type: 'ShortText',
                isEnabled({ contactButtonText }) {
                  return contactButtonText;
                },
              },
              {
                name: 'contactButtonFeatured',
                type: 'Checkbox',
                label: 'Destacar botão de contato',
              },
              {
                name: 'showPrices',
                type: 'Checkbox',
                label: 'Mostrar preços dos quartos',
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'cardBackgroundColor',
                type: 'ColorPicker',
                label: 'Cor do fundo dos cards',
              },
              {
                name: 'cardTextColor',
                type: 'ColorPicker',
                label: 'Cor de texto dos cards',
              },
            ],
            toRemove: ['textColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        adult: '1 adulto | {count} adultos',
        child: '1 criança | {count} crianças',
        night: '1 diária | {count} diárias',
        disclaimer:
          'Valor da diária baseado em uma pesquisa de {nights} para {adults}',
        seeMore: 'Ver mais',
        readLess: 'Ver menos',
        notAvailableTitle: 'Ops, não encontramos nenhum quarto disponível.',
        notAvailableSubtitle:
          'Em breve listaremos aqui todas as nossas acomodações para você escolher a que mais se encaixa no seu perfil.',
      },
      en: {
        adult: '1 adult | {count} adults',
        child: '1 child | {count} children',
        night: '1-night | {count}-nights',
        disclaimer: 'Room rates based on a {nights} stay for {adults}',
        seeMore: 'See more',
        readLess: 'Less',
        notAvailableTitle: 'Ops, we did not find any available room.',
        notAvailableSubtitle:
          "We'll soon list all our rooms here so that you can find the one that best suits your profile.",
      },
      es: {
        adult: '1 adulto | {count} adultos',
        child: '1 niño | {count} niños',
        night: '1 noche | {count} noches',
        disclaimer:
          'Valor de la diaria basado en una busca de {nights} para {adults}',
        seeMore: 'Ver más',
        readLess: 'Menos',
        notAvailableTitle: 'Ops, no encontramos habitaciones disponibles.',
        notAvailableSubtitle:
          'En breve enumeraremos aquí todos nuestros alojamientos para que elijas el que mejor se adapta a tu perfil.',
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.accommodation-list {
  text-align: center;
  overflow: hidden;
  padding: 15px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(sm) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  &__empty {
    text-align: center;
    padding: 20px 20px;

    .empty-image {
      display: inline-block;
      width: 100%;
      max-width: 350px;
      vertical-align: top;

      @include media-breakpoint-down(sm) {
        width: 80%;
        margin-bottom: 40px;
      }
    }
  }

  .content {
    max-width: 1240px;

    .list {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: flex-start;
      gap: 30px;
      margin: 0 auto;

      .room {
        position: relative;
        width: 100%;
        max-width: 380px;
        border-radius: 16px;
        background-color: var(--card-bg-color, $white);

        &__disclaimer {
          position: absolute;
          text-align: right;
          top: 12px;
          right: 14px;
          z-index: 3;

          .icon {
            ::v-deep {
              svg {
                path {
                  fill: #c2c2c2;
                }
              }
            }
          }

          .slb-tooltip {
            right: -10px;
            visibility: hidden;
            opacity: 0;
            color: $lb-dark-gray;
            left: unset;
            right: -10px;

            &.open-up,
            &.open-down {
              opacity: 1;
              visibility: visible;
              transform: translateY(0);
            }
          }
        }

        &__images {
          position: relative;
          border-radius: 16px;

          .guest-max {
            font-weight: $weight-bold;
            font-size: $size-smaller;
            background: $white;
            color: $black;
            top: 12px;
            left: 14px;
            z-index: 3;
            padding: 4px 10px;
            position: absolute;
            border-radius: 24px;
          }

          .modal-button {
            border: none;
            position: absolute;
            opacity: 0;
            left: 0;
            top: 0;
            height: 100%;
            width: 100%;
            z-index: 2;
            background-color: rgba(0, 0, 0, 0.2);
            transition: opacity 0.27s ease-out;
            cursor: pointer;

            svg {
              border: 0;
              background: none;
              position: absolute;
              left: 0;
              top: 0;
              right: 0;
              bottom: 0;
              margin: auto;
              transition: transform 0.25s ease-out;
              transform: scale(0);
              z-index: 3;
            }
          }

          .bg {
            transition: transform 0.3s ease-out;
          }

          @include media-breakpoint-down(sm) {
            .modal-button {
              opacity: 1;

              svg {
                transform: scale(1);
                left: auto;
                top: auto;
                margin: 2px 12px;
                padding: 10px;
                background-color: rgba(0, 0, 0, 0.2);
              }
            }
          }

          @include media-breakpoint-up(sm) {
            .modal-button:hover {
              opacity: 1;

              svg {
                transform: scale(1.1);
              }

              + ul {
                > .bg.swiper-slide-active {
                  transform: scale(1.1);
                }
              }
            }
          }
        }

        &__content {
          text-align: left;
          padding: 24px 16px 16px 16px;
          border-radius: 0 0 16px 16px;
          color: var(--card-text-color, $gray-90);

          .room-name {
            font-size: 18px;
            font-weight: $weight-bold;
            text-align: left;
            height: 22px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .room-description {
            font-size: $size-small;
            text-align: left;
            margin-top: 18px;
            overflow: hidden;
            max-height: calc(3 * 21px);
            min-height: calc(3 * 21px);
            transition: max-height 0.3s ease-in-out;

            &__text {
              line-height: 21px;
            }

            &--empty {
              height: calc(3 * 21px);
            }
          }

          .toggle-see-more {
            font-size: $size-small;
            color: var(--button-color, var(--primary-color));
            text-align: left;
            position: relative;
            border: 0;
            background: none;
            font-weight: $lb-bold;
            outline: inherit;
            padding: 5px 16px 5px 0px;
            cursor: pointer;

            &--hidden {
              visibility: hidden;
              opacity: 0;
            }

            &::after {
              content: '';
              margin-left: 0.625vw;
              display: inline-block;
              border-top: 7px solid;
              border-left: 5px solid transparent !important;
              border-right: 5px solid transparent !important;
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
              transition: transform 0.4s ease-in-out;
            }
          }

          .room-price {
            .price {
              padding: 4px 0;
              font-size: 18px;
              font-weight: $lb-bold;

              ::v-deep .integer {
                font-size: $size-big;
              }

              &--promo {
                font-size: $size-normal;
                text-decoration: line-through;
                text-transform: uppercase;
                margin-left: 10px;
                opacity: 0.7;
              }
            }
          }

          .room-buttons {
            display: flex;
            margin-top: 18px;

            .cta {
              width: 100%;
              border-radius: 8px;
              text-transform: none;
              padding: 10px 6px;
              outline: none;

              & + .cta {
                margin-left: 10px;
              }

              .icon {
                margin-right: 4px;
              }

              .label {
                padding: 10px 14px;
              }
            }
          }

          &--see-more {
            .room-description {
              max-height: 1000px;
            }

            .toggle-see-more {
              &::after {
                transform: translateY(-50%) rotate(180deg);
              }
            }
          }

          &:not(.room__content--see-more) {
            .room-description {
              &__text {
                display: -webkit-box;
                -webkit-box-orient: vertical;
              }
            }
          }
        }
      }
    }
  }
}

// Swipper CSS | Accommodation images
.swiper {
  .swiper-wrapper {
    height: 220px;

    .swiper-slide {
      height: 220px;
      position: relative;
    }
  }

  .custom-button-prev,
  .custom-button-next {
    background: $white;
    top: calc(50% - 16px);
    width: 32px;
    height: 32px;
    z-index: 10;
    position: absolute;
    padding: 4px;
    border-radius: 24px;
    cursor: pointer;

    @include media-breakpoint-down(sm) {
      opacity: 1;
    }

    &.swiper-button-disabled {
      display: none;
      cursor: initial;
    }

    .button-icon {
      color: $black !important;
      top: calc(50% - 9px);
      width: auto;
    }
  }

  .custom-button-prev {
    left: 14px;
    text-align: right;

    .button-icon {
      left: auto;
      right: 10px;
    }
  }

  .custom-button-next {
    right: 14px;
    text-align: left;

    .button-icon {
      left: 10px;
    }
  }
}

// Modal accommodations
.modal-room {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background: $white;

  &__close {
    top: 48px;
    right: 64px;
    height: 38px;
    width: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    outline: inherit;
    position: absolute;
    border: 1px solid $black;
    background: $white;
    border-radius: 100%;
    z-index: 3;
  }

  &__container {
    height: 100%;
    position: relative;
  }

  &__content {
    width: 100%;
    max-width: 1366px;
    padding: 120px 20px;
    display: inline-block;

    &__name {
      width: 100%;
      font-size: 20px;
      color: $black;
      font-weight: $lb-bold;
      text-align: left;
      margin-bottom: 28px;
      transition: opacity 0.2s;
    }

    &__amenities {
      text-align: left;
      display: inline-block;
      width: 250px;

      .list-amenities {
        &__item {
          display: block;
          font-size: $size-normal;
          color: $black;
          font-weight: $lb-semibold;
          margin-bottom: 10px;
        }
      }
    }

    &__images {
      display: inline-block;
      width: 100%;
      max-width: 600px;
      vertical-align: top;

      .list-images {
        &__item {
          width: 100%;
          height: auto;
          margin-bottom: 10px;
        }
      }
    }
  }

  @include media-breakpoint-down(lg) {
    &__close {
      top: 20px;
      right: 16px;
    }

    &__content {
      padding: 60px 0px;

      &__amenities {
        width: 100%;
        max-width: 600px;
        padding: 0 16px;
        margin-bottom: 20px;

        .list-amenities {
          &__item {
            display: inline-block;
            font-size: $size-small;

            &::before {
              content: '\00B7';
              font-size: 18px;
              display: inline-block;
              margin: 0 7px;
            }
          }
        }
      }
    }
  }
}
</style>
